import { useState, useEffect } from "react";
import { updateLinksToOpenInNewTabs } from "@/utils/product/updateLinksToOpenInNewTab";
import { SecondaryButton } from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import { TickOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/TickOutlined";
import { Message as InfoMessage } from "@JohnLewisPartnership/jl-design-system/dist/components/message/Message";
import { removePrecedingLineBreaks } from "@/utils/product/cleanPromoDescription";
import TermsAndConditionsAccordion from "../../../__shared__/TermsAndConditionsAccordion/TermsAndConditionsAccordion";
import GiftWithPurchase from "../GiftWithPurchase/GiftWithPurchase";
import styles from "./SpecialOffersPromotion.module.scss";
import { getTargetProductId } from "@/utils/product/getProductFromPromoMessaging";
import { findPromoCode } from "@/utils/product/getPromoCode";
import { Product, Variant } from "@/types/types";
import { getGiftProductData } from "@/utils/product/getGiftProductData";
import { dispatchMonetateQEvent } from "@/utils/monetate/monetateQ.ts";
import SpendStretch from "../SpendStretch/SpendStretch";
import { MessageWithCategoryAndType } from "../../../../utils/product/getPromoMessaging.ts";
import cx from "classnames";
import { SaleTagIcon } from "@/components/__shared__/SaleTagIcon/SaleTagIcon.tsx";
import {
  getPromoTitleFromDescription,
  stripHTML,
  getDescriptionStartingWithClaim,
} from "../../utils.ts";

interface SpecialOffersPromotionProps {
  promotion: MessageWithCategoryAndType;
  drawerIsOpen: boolean;
  onClose: () => void;
  productHasOneFreeGift?: boolean;
  prodCatEndpoint?: string;
  prodCatApiKey?: string;
  basketEndpoint?: string;
  specialOffersVariant?: string;
  applyNewDrawerUI?: boolean;
}

const SpecialOffersPromotion = ({
  promotion,
  drawerIsOpen,
  onClose,
  productHasOneFreeGift = false,
  prodCatEndpoint,
  prodCatApiKey,
  basketEndpoint,
  specialOffersVariant,
  applyNewDrawerUI = false,
}: SpecialOffersPromotionProps) => {
  const [buttonIsClicked, setButtonIsClicked] = useState(false);
  const [targetProduct, setTargetProduct] = useState<Product | null>(null);

  const copyPromoCode = (promoCode: string) => {
    navigator.clipboard.writeText(promoCode);
    setButtonIsClicked(true);
    dispatchMonetateQEvent("COPY_PROMO_BUTTON_CLICKED");
  };

  let promoCode = findPromoCode(promotion.title);
  if (!promoCode) {
    promoCode = findPromoCode(promotion.description);
  }

  const descriptionWithoutLineBreaks = removePrecedingLineBreaks(
    promotion.description,
  );
  const updatedLinksDescription = updateLinksToOpenInNewTabs(
    descriptionWithoutLineBreaks,
  );

  const [description, terms] = updatedLinksDescription.split(
    "<strong>Terms and conditions</strong>",
  );

  const isTradeInPromotion = promotion?.promotionType === "Trade In - Enhanced";

  const giftProductId =
    description && productHasOneFreeGift
      ? getTargetProductId(description)
      : null;
  const isGiftOutOfStock = !!(
    targetProduct &&
    targetProduct?.variants.every(
      (variant: Variant) => !variant?.availability?.availableToOrder,
    )
  );

  const showPromoCodeButton =
    promoCode && (!targetProduct || !isGiftOutOfStock);

  const showGiftWithPurchase = !!(targetProduct && productHasOneFreeGift);

  const spendStretchPromoTypes = [
    "Spend Stretch- £ off ",
    "Spend Stretch- £ off",
    "Spend Stretch- GWP ",
    "Spend Stretch- GWP",
  ];
  const showSpendStretch =
    promoCode && spendStretchPromoTypes.includes(promotion.promotionType);

  const promotionTitle = getPromoTitleFromDescription(
    promotion.title,
    description || "",
  );

  const cleanPromotionTitle = stripHTML(promotionTitle);

  const promoDescription = getDescriptionStartingWithClaim(
    description || "",
    promotionTitle,
  );

  const promoTitleClasses = cx(
    styles.SpecialOffersDrawer__promoTitle,
    applyNewDrawerUI && styles.SpecialOffersDrawer__promoTitleWithBorder,
  );

  useEffect(() => {
    if (giftProductId && prodCatEndpoint && prodCatApiKey) {
      const getProdCatProduct = async (productId: string) => {
        const giftProduct = await getGiftProductData(
          productId,
          prodCatEndpoint,
          prodCatApiKey,
        );
        if (giftProduct) {
          setTargetProduct(giftProduct);
        }
      };
      getProdCatProduct(giftProductId);
    }
  }, [giftProductId]);

  return (
    <div
      className={cx(
        styles.SpecialOffersDrawer__promoTop,
        applyNewDrawerUI
          ? styles.SpecialOffersDrawer__promoBottomVariant
          : styles.SpecialOffersDrawer__promoBottom,
      )}
      data-testid="special-offers-drawer-promotion"
    >
      {specialOffersVariant === "A" && (
        <div
          className={
            applyNewDrawerUI ? styles.tagBadgeWithoutChip : styles.tagBadge
          }
          data-testid="special-offers-drawer-promotion-badge"
        >
          <SaleTagIcon type="small" />
          {promotion.tag}
        </div>
      )}
      <h3
        className={promoTitleClasses}
        data-testid="special-offers-drawer-promotion-title"
      >
        {applyNewDrawerUI && specialOffersVariant !== "A" && (
          <SaleTagIcon type="small" />
        )}
        <span dangerouslySetInnerHTML={{ __html: cleanPromotionTitle }} />
      </h3>

      {promoDescription && (
        <>
          <div
            className={styles.SpecialOffersDrawer__promoDescription}
            dangerouslySetInnerHTML={{
              __html: promoDescription,
            }}
          />
          {showSpendStretch && (
            <SpendStretch
              promotion={promotion}
              basketEndpoint={basketEndpoint || ""}
              targetProductId={giftProductId || ""}
              drawerIsOpen={drawerIsOpen}
            />
          )}
          {promoCode && showGiftWithPurchase && (
            <GiftWithPurchase
              targetProduct={targetProduct}
              promoCode={promoCode}
              drawIsOpen={drawerIsOpen}
              basketEndpoint={basketEndpoint || ""}
              isGiftOutOfStock={isGiftOutOfStock}
            />
          )}
        </>
      )}
      {showPromoCodeButton && (
        <>
          <SecondaryButton
            disabled={buttonIsClicked || !drawerIsOpen}
            id={`copyPromoButton${promoCode}`}
            className={styles.SpecialOffersDrawer__newCopyPromoButton}
            onClick={() => copyPromoCode(promoCode || "")}
          >
            {buttonIsClicked && <TickOutlined size="medium" />}
            {buttonIsClicked ? "Copied" : "Copy promo code"}
          </SecondaryButton>
          {buttonIsClicked && (
            <InfoMessage
              body="Use promo code at Checkout to apply offer"
              type="information"
              className={styles.SpecialOffersDrawer__copyPromoMessage}
            />
          )}
        </>
      )}
      {terms && (
        <TermsAndConditionsAccordion
          terms={terms}
          isGiftWithPurchaseEnabled={showGiftWithPurchase}
          applyNewDrawerUI={applyNewDrawerUI}
        />
      )}
      {isTradeInPromotion && (
        <div
          data-testid="special-offer-buttons"
          className={styles.SpecialOffersDrawer__buttonContainer}
        >
          {isTradeInPromotion && (
            <SecondaryButton
              className={styles.SpecialOffersDrawer__tradeInCTAButton}
              onClick={onClose}
            >
              Trade in device
            </SecondaryButton>
          )}
        </div>
      )}
    </div>
  );
};

export default SpecialOffersPromotion;
